/**
 * Configuration utility functions
 */

import type { BabylonConfig, PartialBabylonConfig } from "./schemas";

/**
 * Deep merge two objects
 * Arrays are replaced, not merged
 * @param target Target object
 * @param source Source object to merge
 */
function deepMerge<T extends Record<string, unknown>>(
  target: T,
  source: Partial<T>,
): T {
  const result = { ...target };

  for (const key in source) {
    const sourceValue = source[key];
    const targetValue = target[key];

    if (sourceValue === undefined) {
      continue;
    }

    if (sourceValue === null) {
      result[key] = null as T[Extract<keyof T, string>];
      continue;
    }

    // Replace arrays entirely
    if (Array.isArray(sourceValue)) {
      result[key] = [...sourceValue] as T[Extract<keyof T, string>];
      continue;
    }

    // Deep merge objects
    if (
      typeof sourceValue === "object" &&
      typeof targetValue === "object" &&
      targetValue !== null &&
      !Array.isArray(targetValue)
    ) {
      result[key] = deepMerge(
        targetValue as Record<string, unknown>,
        sourceValue as Record<string, unknown>,
      ) as T[Extract<keyof T, string>];
      continue;
    }

    // Replace primitives and other types
    result[key] = sourceValue as T[Extract<keyof T, string>];
  }

  return result;
}

/**
 * Merge configurations with proper deep merging
 * @param base Base configuration
 * @param override Configuration overrides
 */
export function mergeConfigs(
  base: BabylonConfig,
  override: PartialBabylonConfig,
): BabylonConfig {
  return deepMerge(base, override);
}
